import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
    baseURL: API,
    timeout: 120000,
});

/**
 * Build an absolute URL for a backend path returned by the API.
 *
 * The file-conversion endpoints return a **relative** `download_url`
 * (e.g. `/api/tools/download/abc123`) because the frontend and backend are
 * always same-origin behind Nginx.  When the caller sets
 * `window.location.href = downloadUrl` a relative URL resolves against the
 * current page origin, which is exactly what we want — no host hardcoding.
 *
 * But some contexts (opening a new tab, passing to a service worker, etc.)
 * benefit from an absolute URL, so we expose a small helper that prepends
 * `REACT_APP_BACKEND_URL` when the input starts with `/api/`. Anything
 * already absolute is returned unchanged.
 */
export function absoluteApiUrl(pathOrUrl) {
    if (!pathOrUrl) return '';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    if (pathOrUrl.startsWith('/api/')) {
        return `${BACKEND_URL}${pathOrUrl}`;
    }
    return pathOrUrl;
}

export async function getBlogPosts() {
    const { data } = await http.get('/blog/posts');
    return data;
}

export async function getBlogPost(slug) {
    const { data } = await http.get(`/blog/posts/${slug}`);
    return data;
}

export async function submitContact(payload) {
    const { data } = await http.post('/contact', payload);
    return data;
}

// ---------------------------------------------------------------------------
// File-conversion endpoints
//
// These now return a JSON envelope instead of a Blob:
//
//   {
//     download_id:   "hex",
//     download_url:  "/api/tools/download/<id>",   // relative → same-origin safe
//     filename:      "input.pdf",
//     size_bytes:    12345,
//     media_type:    "application/pdf",
//     …tool-specific extras (original_size, compressed_size, ratio, width, height)
//   }
//
// The caller navigates to `absoluteApiUrl(download_url)` (or just
// `download_url`) to trigger the browser's native download flow.
// ---------------------------------------------------------------------------

export async function convertWordToPdf(file, onProgress) {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await http.post('/tools/word-to-pdf', fd, {
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
    });
    return data;
}

export async function convertJpgToPng(file, onProgress) {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await http.post('/tools/jpg-to-png', fd, {
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
    });
    return data;
}

export async function compressImageApi(file, quality, onProgress) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('quality', String(quality));
    const { data } = await http.post('/tools/image-compress', fd, {
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
    });
    return data;
}

export async function getImageDimensions(file) {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await http.post('/tools/image-dimensions', fd);
    return data;
}

export async function resizeImageApi(file, opts, onProgress) {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.width) fd.append('width', String(opts.width));
    if (opts.height) fd.append('height', String(opts.height));
    fd.append('keep_aspect', opts.keepAspect ? 'true' : 'false');
    fd.append('fit_mode', opts.fitMode || 'contain');
    const { data } = await http.post('/tools/resize-image', fd, {
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
    });
    return data;
}

export async function checkPageSpeed(url) {
    const { data } = await http.post('/tools/page-speed', { url });
    return data;
}

export async function getThumbnailInfo(url) {
    const { data } = await http.post('/tools/thumbnail-info', { url });
    return data;
}

export function thumbnailDownloadUrl(videoId, quality) {
    return `${API}/tools/thumbnail-download?video_id=${encodeURIComponent(videoId)}&quality=${encodeURIComponent(quality)}`;
}

export function extractErrorMessage(err) {
    if (!err) return 'Unknown error';
    if (err.response && err.response.data) {
        const d = err.response.data;
        // Kept for tools that might still receive blob-shaped errors
        // (none of the current endpoints do after the two-step refactor,
        // but leaving the guard costs nothing and is future-proof).
        if (d instanceof Blob) return null;
        if (typeof d === 'string') return d;
        if (d.detail) return d.detail;
        try {
            return JSON.stringify(d);
        } catch (e) {
            return String(d);
        }
    }
    return err.message || 'Request failed';
}

export async function parseBlobError(blob) {
    try {
        const text = await blob.text();
        try {
            const json = JSON.parse(text);
            return json.detail || text;
        } catch {
            return text || 'Server error';
        }
    } catch {
        return 'Server error';
    }
}
